import type { ReactNode } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { can, normalizeRole, permissionReason, type CapabilityName, type Role } from '@/lib/permissions'
import { roleLabel } from '@/lib/labels'

/**
 * `usePermission('quotes.create')` → true when the signed-in person may do it.
 * Uses `user.capabilities` from the session when the server sends them, else
 * the role ladder in lib/permissions.ts (mirrors backend requireRole()).
 */
export function usePermission(capability: CapabilityName): boolean {
  return useAuthStore((s) => can(s.user, capability))
}

/** Several checks at once plus the role, for components that gate many things. */
export function usePermissions(): {
  can: (capability: CapabilityName) => boolean
  role: Role
  /** "Admin", "Office", "Crew", "Viewer" (or the custom role's name) */
  roleLabel: string
  reason: (capability: CapabilityName) => string
} {
  const user = useAuthStore((s) => s.user)
  return {
    can: (capability) => can(user, capability),
    role: normalizeRole(user?.role),
    roleLabel: user?.roleName || roleLabel(user?.role),
    reason: permissionReason,
  }
}

/**
 * Render children only when allowed.
 *
 *   <Can do="quotes.create"><button>New quote</button></Can>
 *   <Can do="settings.team" fallback={<p>Only admins can invite people.</p>}>…</Can>
 */
export function Can({ do: capability, children, fallback = null }: { do: CapabilityName; children: ReactNode; fallback?: ReactNode }) {
  const allowed = usePermission(capability)
  return <>{allowed ? children : fallback}</>
}
